import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Avatar,
  Divider,
} from "@mui/material";
import { keyframes } from "@mui/system";

import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import ArticleIcon from "@mui/icons-material/Article";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CallIcon from "@mui/icons-material/Call";
import ChatIcon from "@mui/icons-material/Chat";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import { useNavigate } from "react-router-dom";

// Premium Animations
const fadeInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-50px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(50px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

// Transition component
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function UserContactManagement() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterBuyer, setFilterBuyer] = useState("");
  const [filterSeller, setFilterSeller] = useState("");
  const [filterContactType, setFilterContactType] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const token = localStorage.getItem("token");
  const metaRed = "#C62828";
  const metaGreen = "#2E7D32";
  const metaBlue = "#1565C0";

  // Fetch all contacts
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/user-contact/get-contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setContacts(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch contacts", "error");
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      showSnackbar(error.response?.data?.message || "Error fetching contacts", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle view dialog open
  const handleViewDialog = (contact) => {
    setSelectedContact(contact);
    setViewDialogOpen(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterBuyer("");
    setFilterSeller("");
    setFilterContactType("");
  };

  // Get unique buyers and sellers for filters
  const uniqueBuyers = useMemo(() => {
    const buyers = new Set();
    contacts.forEach((contact) => {
      if (contact.buyer && contact.buyer[0]?.name) {
        buyers.add(contact.buyer[0].name);
      }
    });
    return Array.from(buyers);
  }, [contacts]);

  const uniqueSellers = useMemo(() => {
    const sellers = new Set();
    contacts.forEach((contact) => {
      if (contact.seller && contact.seller[0]?.name) {
        sellers.add(contact.seller[0].name);
      }
    });
    return Array.from(sellers);
  }, [contacts]);

  // Filtered contacts
  const filteredContacts = useMemo(() => {
    let filtered = [...contacts];

    if (filterBuyer) {
      filtered = filtered.filter(
        (item) =>
          item.buyer &&
          item.buyer[0]?.name &&
          item.buyer[0].name.toLowerCase().includes(filterBuyer.toLowerCase())
      );
    }

    if (filterSeller) {
      filtered = filtered.filter(
        (item) =>
          item.seller &&
          item.seller[0]?.name &&
          item.seller[0].name.toLowerCase().includes(filterSeller.toLowerCase())
      );
    }

    if (filterContactType) {
      filtered = filtered.filter((item) => item.contact_type === filterContactType);
    }

    return filtered;
  }, [contacts, filterBuyer, filterSeller, filterContactType]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get contact type chip
  const getContactTypeChip = (type) => {
    const config = {
      call: { icon: <CallIcon />, color: "#4caf50", bg: "#e8f5e9", label: "CALL" },
      whatsapp: { icon: <WhatsAppIcon />, color: "#25D366", bg: "#e8f5e9", label: "WHATSAPP" },
      chat: { icon: <ChatIcon />, color: "#2196f3", bg: "#e3f2fd", label: "CHAT" },
    };
    const { icon, color, bg, label } = config[type] || config.call;
    return (
      <Chip
        icon={icon}
        label={label}
        size="small"
        sx={{ bgcolor: bg, color: color, fontWeight: "bold" }}
      />
    );
  };

  const columns = [
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       S.No
    //     </MDTypography>
    //   ),
    //   accessor: "sno",
    //   align: "center",
    //   width: "5%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          BUYER
        </MDTypography>
      ),
      accessor: "buyer",
      align: "left",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          SELLER
        </MDTypography>
      ),
      accessor: "seller",
      align: "left",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POST
        </MDTypography>
      ),
      accessor: "post",
      align: "left",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CONTACT TYPE
        </MDTypography>
      ),
      accessor: "contact_type",
      align: "center",
      //   width: "12%",
    },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       PRICE
    //     </MDTypography>
    //   ),
    //   accessor: "price",
    //   align: "center",
    //   width: "8%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          DATE
        </MDTypography>
      ),
      accessor: "createdAt",
      align: "center",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      //   width: "10%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredContacts.map((contact, index) => ({
        sno: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {index + 1}
          </MDTypography>
        ),
        buyer: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              src={contact.buyer?.[0]?.profile_photo}
              sx={{ width: 32, height: 32, border: `1px solid ${metaGreen}` }}
            >
              <PersonIcon />
            </Avatar>
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" fontSize="0.75rem">
                {contact.buyer?.[0]?.name || "N/A"}
              </MDTypography>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                color="text"
                display="block"
                fontSize="0.65rem"
              >
                {contact.buyer?.[0]?.email_id}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        seller: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              src={contact.seller?.[0]?.profile_photo}
              sx={{ width: 32, height: 32, border: `1px solid ${metaRed}` }}
            >
              <StoreIcon />
            </Avatar>
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" fontSize="0.75rem">
                {contact.seller?.[0]?.name || "N/A"}
              </MDTypography>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                color="text"
                display="block"
                fontSize="0.65rem"
              >
                {contact.seller?.[0]?.email_id}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        post: (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold" fontSize="0.75rem">
              {contact.post?.[0]?.title || "N/A"}
            </MDTypography>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              color="text"
              display="block"
              fontSize="0.65rem"
            >
              Post Type : {contact.post?.[0]?.post_type}
            </MDTypography>
          </MDBox>
        ),
        contact_type: getContactTypeChip(contact.contact_type),
        price: (
          <MDTypography variant="button" fontWeight="bold" color="error">
            ₹{contact.post?.[0]?.price || 0}
          </MDTypography>
        ),
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(contact.createdAt)}
          </MDTypography>
        ),
        action: (
          <Tooltip title="View Details">
            <IconButton
              color="info"
              onClick={() => navigate(`/usercontact-details/${contact._id}`)}
              size="small"
              sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      })),
    [filteredContacts]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <PhoneIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    User Contact Management
                  </MDTypography>
                </MDBox>
                {/* <MDButton
                  variant="gradient"
                  color="dark"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ borderRadius: "12px", textTransform: "none" }}
                >
                  Filters
                </MDButton> */}
              </MDBox>

              {/* Filters Section */}
              {!showFilters && (
                <Fade in={!showFilters}>
                  <Box sx={{ pt: 3, pl: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by Buyer"
                          value={filterBuyer}
                          onChange={(e) => setFilterBuyer(e.target.value)}
                          placeholder="Enter buyer name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by Seller"
                          value={filterSeller}
                          onChange={(e) => setFilterSeller(e.target.value)}
                          placeholder="Enter seller name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          value={filterContactType}
                          onChange={(e) => setFilterContactType(e.target.value)}
                          variant="outlined"
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                          <option value="">All Types</option>
                          <option value="call">Call</option>
                          <option value="whatsapp">WhatsApp</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                          fullWidth
                          sx={{ borderRadius: "10px", height: "40px" }}
                        >
                          Clear
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredContacts.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No contacts found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default UserContactManagement;
